import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { fetchAuditLogs, type AuditLogEntry } from '@/lib/auditLogApi';
import {
  CaretRight,
  CaretDown,
  Plus,
  PencilSimple,
  Trash,
  SignIn,
  Download,
  Briefcase,
  Gavel,
  Users,
  FileText,
  CurrencyGbp,
  CalendarBlank,
  ChatTeardropDots,
  ClockCounterClockwise,
  ArrowRight,
  CircleNotch,
} from '@phosphor-icons/react';

const ACTION_ICON: Record<string, React.ElementType> = {
  create: Plus, update: PencilSimple, delete: Trash,
  login: SignIn, download: Download,
};

const ENTITY_ICON: Record<string, { Icon: React.ElementType; color: string }> = {
  Case:          { Icon: Briefcase,             color: 'text-brand-600 dark:text-brand-400 bg-brand-50' },
  Matter:        { Icon: Briefcase,             color: 'text-brand-600 dark:text-brand-400 bg-brand-50' },
  Hearing:       { Icon: Gavel,                 color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10' },
  Contact:       { Icon: Users,                 color: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10' },
  Document:      { Icon: FileText,              color: 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800' },
  FeeRecord:     { Icon: CurrencyGbp,           color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10' },
  FeeNote:       { Icon: CurrencyGbp,           color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10' },
  CalendarEvent: { Icon: CalendarBlank,         color: 'text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10' },
  Task:          { Icon: ClockCounterClockwise, color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10' },
  Message:       { Icon: ChatTeardropDots,      color: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10' },
};

const ACTION_LABEL: Record<string, string> = {
  create: 'created', update: 'updated', delete: 'deleted',
  login: 'logged in', logout: 'logged out', download: 'downloaded',
  export: 'exported', share: 'shared',
};

const ENTITY_LABEL: Record<string, string> = {
  Case: 'matter', Matter: 'matter', Hearing: 'hearing',
  Contact: 'contact', Document: 'document', FeeRecord: 'fee record',
  FeeNote: 'fee note', CalendarEvent: 'calendar event', Task: 'task',
  Message: 'message',
};

const SKIP_FIELDS = new Set(['updatedAt', 'createdAt', 'id', 'chambersId', 'deletedAt']);

const FIELD_LABEL: Record<string, string> = {
  caseName: 'Matter name', caseNumber: 'Matter number', status: 'Status',
  description: 'Description', feeType: 'Fee type', feeEstimate: 'Fee estimate',
  leadBarristerId: 'Lead barrister', outcome: 'Outcome', hearingDate: 'Hearing date',
  hearingType: 'Hearing type', firstName: 'First name', lastName: 'Last name',
  email: 'Email', memberType: 'Role', amount: 'Amount', totalAmount: 'Total',
  isBillable: 'Billable', title: 'Title', priority: 'Priority',
  dueDate: 'Due date', assignedToId: 'Assigned to', internalNotes: 'Internal notes',
};

function fmtTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return v.replace(/_/g, ' ');
  }
  return JSON.stringify(v);
}

function actorName(log: AuditLogEntry) {
  if (log.actor) return `${log.actor.firstName} ${log.actor.lastName}`;
  return log.actorEmail ?? 'System';
}

function actorInitials(log: AuditLogEntry) {
  if (log.actor) return `${log.actor.firstName[0]}${log.actor.lastName[0]}`;
  return 'SY';
}

function ChangeDiff({ log }: { log: AuditLogEntry }) {
  const before = log.before as Record<string, unknown> | null;
  const after  = log.after  as Record<string, unknown> | null;

  const diffRows: { field: string; before: unknown; after: unknown }[] = [];

  if (log.action === 'update' && before && after) {
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of allKeys) {
      if (SKIP_FIELDS.has(key)) continue;
      if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
        diffRows.push({ field: key, before: before[key], after: after[key] });
      }
    }
  } else if (log.action === 'create' && after) {
    for (const [key, val] of Object.entries(after)) {
      if (SKIP_FIELDS.has(key) || val === null || val === undefined) continue;
      diffRows.push({ field: key, before: undefined, after: val });
    }
  } else if (log.action === 'delete' && before) {
    for (const [key, val] of Object.entries(before)) {
      if (SKIP_FIELDS.has(key) || val === null || val === undefined) continue;
      diffRows.push({ field: key, before: val, after: undefined });
    }
  }

  if (diffRows.length === 0) {
    return <p className="py-2 text-xs italic text-muted-foreground">No detail recorded for this action.</p>;
  }

  const isUpdate = log.action === 'update';
  const isCreate = log.action === 'create';

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Field</th>
            {isUpdate && <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Before</th>}
            <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
              {isUpdate ? 'After' : isCreate ? 'Value' : 'Had been'}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {diffRows.map(({ field, before: bv, after: av }) => (
            <tr key={field} className="bg-card">
              <td className="px-3 py-2 font-medium text-foreground">
                {FIELD_LABEL[field] ?? field.replace(/([A-Z])/g, ' $1').trim()}
              </td>
              {isUpdate && (
                <td className="px-3 py-2 text-red-600 dark:text-red-400 line-through decoration-red-300">{fmtVal(bv)}</td>
              )}
              <td className={`px-3 py-2 ${isUpdate ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-foreground'}`}>
                {fmtVal(isCreate ? av : bv)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryRow({ log, basePath, matterId }: { log: AuditLogEntry; basePath: string; matterId: string }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const entityMeta = ENTITY_ICON[log.entityType ?? ''] ?? { Icon: Briefcase, color: 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800' };
  const { Icon, color } = entityMeta;
  const ActionIcon = ACTION_ICON[log.action] ?? PencilSimple;
  const name = actorName(log);
  const initials = actorInitials(log);

  const after = log.after as Record<string, unknown> | null;
  const entityName = (after?.caseName ?? after?.caseNumber ?? after?.name ?? after?.title ?? after?.subject ?? '') as string;

  let entityRoute: string | null = null;
  if (log.entityType === 'Hearing' && log.entityId) entityRoute = `${basePath}/hearings/${log.entityId}`;
  // This panel only ever lists logs for the matter being viewed (it queries by this matterId),
  // so an "Open record" arrow on a Case/Matter row pointed at the page the user was already on.
  // Hearings are a different record and the arrow is worth having there.
  if ((log.entityType === 'Case' || log.entityType === 'Matter') && log.entityId && log.entityId !== matterId) {
    entityRoute = `${basePath}/matters/${log.entityId}`;
  }

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-muted/30"
      >
        <span className="mt-2.5 shrink-0 text-muted-foreground/50">
          {expanded ? <CaretDown className="h-3.5 w-3.5" /> : <CaretRight className="h-3.5 w-3.5" />}
        </span>

        <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${color}`}>
          <Icon className="h-4 w-4" weight="duotone" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm leading-snug text-foreground">
            <span className="font-semibold">{name}</span>
            {' '}
            <span className="text-muted-foreground">
              {ACTION_LABEL[log.action] ?? log.action}
              {log.entityType ? ` a ${ENTITY_LABEL[log.entityType] ?? log.entityType.toLowerCase()}` : ''}
            </span>
            {entityName && <span className="font-medium text-foreground"> — {entityName}</span>}
          </p>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span>{fmtTime(log.createdAt)}</span>
            <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-medium">
              <ActionIcon className="h-3 w-3" />
              {log.action}
            </span>
            {log.actor?.memberType && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-medium">
                {log.actor.memberType.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>

        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700 dark:text-brand-300">
          {initials}
        </div>

        {entityRoute && (
          <button
            type="button"
            title="Open record"
            className="mt-1.5 shrink-0 rounded p-1 text-muted-foreground/50 transition hover:bg-muted hover:text-brand-600 dark:hover:text-brand-400"
            onClick={(e) => { e.stopPropagation(); navigate(entityRoute!); }}
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </button>

      {expanded && (
        <div className="border-t border-border/60 bg-muted/20 px-14 py-4">
          <ChangeDiff log={log} />
        </div>
      )}
    </div>
  );
}

export function MatterHistoryPanel({ matterId }: { matterId: string }) {
  const user = useAuthStore((s) => s.user);
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isBarrister ? '/barrister' : isClerk ? '/clerk' : '/admin';

  const { data, isLoading } = useQuery({
    queryKey: ['matter-history', matterId],
    queryFn: () => fetchAuditLogs({ entityId: matterId, limit: 50 }),
    staleTime: 30_000,
  });

  const logs = data?.logs ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <CircleNotch className="h-5 w-5 animate-spin" />
        Loading history…
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No recorded changes for this matter yet.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      {logs.map((log) => (
        <HistoryRow key={log.id} log={log} basePath={basePath} matterId={matterId} />
      ))}
    </div>
  );
}
